import { getDriver } from './drivers.js';
import { mountHotspotOverlay } from './hotspot/mountHotspotOverlay.jsx';
import {
  applyHotspotOverlayPlacement,
  computeOverlayPlacement,
  getVenueHeaderBottom,
  projectMarkerToScreen,
  resetHotspotOverlayPlacement,
} from './hotspotOverlayPosition.js';

/** @type {ReturnType<typeof mountHotspotOverlay> | null} */
let overlayApi = null;

/** True while the overlay is visible or playing its exit animation. */
let overlayOnScreen = false;

const mountEl = document.getElementById('hotspot-overlay-root');
if (mountEl) {
  overlayApi = mountHotspotOverlay(mountEl, {
    onExitComplete() {
      overlayOnScreen = false;
      clearHotspotOverlayPosition();
    },
  });
}

export function isHotspotOverlayOnScreen() {
  return overlayOnScreen;
}

/**
 * @param {{ markerName: string; driver: import('./drivers.js').Driver; description: string; cornerKey?: string }} payload
 */
export function showHotspotOverlay({ markerName, driver, description, cornerKey }) {
  overlayOnScreen = true;
  overlayApi?.show({
    markerName,
    driver,
    description,
    cornerKey: cornerKey ?? markerName,
  });
}

export function hideHotspotOverlay() {
  overlayApi?.hide();
}

/**
 * @param {THREE.Object3D} marker
 * @param {THREE.Camera} camera
 * @param {HTMLCanvasElement} canvas
 * @param {number} [beamHeight=16]
 */
export function updateHotspotOverlayPosition(
  marker,
  camera,
  canvas,
  beamHeight = 16,
) {
  const screen = projectMarkerToScreen(marker, camera, canvas, beamHeight);
  const placement = computeOverlayPlacement(
    screen,
    {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    { headerBottom: getVenueHeaderBottom() },
  );
  applyHotspotOverlayPlacement(mountEl, placement);
}

export function clearHotspotOverlayPosition() {
  resetHotspotOverlayPlacement(mountEl);
}

/**
 * @param {THREE.Group | null} marker
 */
export function updateHotspotOverlayFromMarker(marker) {
  if (!marker) {
    hideHotspotOverlay();
    return;
  }

  const driverId = marker.userData.driverId;
  if (!driverId) {
    hideHotspotOverlay();
    return;
  }

  const driver = getDriver(driverId);
  if (!driver) {
    hideHotspotOverlay();
    return;
  }

  const index = marker.userData.cornerIndex ?? 0;
  const markerName =
    marker.userData.cornerName ?? 'Marker name';
  const description = marker.userData.description ?? '';
  const cornerKey =
    marker.userData.cornerKey ?? `corner-${index}`;

  showHotspotOverlay({ markerName, driver, description, cornerKey });
}
